import { useEffect, useRef } from 'react';
import {
  NamedSseEvent,
  PersistedTaskSnapshot,
  readNamedSseStream,
  TaskEventPayload,
  taskApi,
} from '../services/api';

export const ACTIVE_TASK_STATUSES = new Set([0, 10, 20, 60, 80, 90]);
const RECONNECT_INTERVAL_MS = 3000;

const isAbortError = (error: unknown): boolean => {
  if (error instanceof DOMException && error.name === 'AbortError') return true;
  if (error instanceof Error && error.message.includes('aborted')) return true;
  return false;
};

export const useTaskSubscription = (
  task: PersistedTaskSnapshot | null,
  onSnapshot: (snapshot: PersistedTaskSnapshot) => void,
  onSettled: () => void
) => {
  const onSnapshotRef = useRef(onSnapshot);
  const onSettledRef = useRef(onSettled);
  const taskId = task?.id;
  const taskStatus = task?.status;

  useEffect(() => {
    onSnapshotRef.current = onSnapshot;
    onSettledRef.current = onSettled;
  }, [onSettled, onSnapshot]);

  useEffect(() => {
    if (!taskId || taskStatus === undefined || !ACTIVE_TASK_STATUSES.has(taskStatus)) {
      return undefined;
    }

    const stateRef = {
      disposed: false,
      settled: false,
      lastEventId: undefined as string | undefined,
      reconnectTimer: undefined as number | undefined,
    };
    const controller = new AbortController();

    const handleEvent = (event: NamedSseEvent<TaskEventPayload>) => {
      stateRef.lastEventId = event.id || event.data.event_id || stateRef.lastEventId;
      if (event.data.task_id !== taskId) return;
      onSnapshotRef.current(event.data.task);
      if (!ACTIVE_TASK_STATUSES.has(event.data.task_status)) {
        stateRef.settled = true;
        onSettledRef.current();
        controller.abort();
      }
    };

    const waitForReconnect = () => new Promise<void>((resolve) => {
      stateRef.reconnectTimer = window.setTimeout(resolve, RECONNECT_INTERVAL_MS);
    });

    const subscribe = async () => {
      while (!stateRef.disposed && !stateRef.settled) {
        try {
          const response = await taskApi.events(
            taskId,
            stateRef.lastEventId,
            controller.signal
          );
          await readNamedSseStream<TaskEventPayload>(
            response,
            handleEvent,
            '任务进度订阅失败'
          );
        } catch (error) {
          if (stateRef.disposed || stateRef.settled || controller.signal.aborted || isAbortError(error)) return;
          try {
            const response = await taskApi.get(taskId);
            onSnapshotRef.current(response.data.data);
            if (!ACTIVE_TASK_STATUSES.has(response.data.data.status)) {
              stateRef.settled = true;
              onSettledRef.current();
              return;
            }
          } catch {
            // Keep the last persisted snapshot and retry the subscription.
          }
        }
        if (!stateRef.disposed && !stateRef.settled) await waitForReconnect();
      }
    };

    void subscribe();
    return () => {
      stateRef.disposed = true;
      if (stateRef.reconnectTimer !== undefined) {
        window.clearTimeout(stateRef.reconnectTimer);
      }
      controller.abort();
    };
  }, [taskId, taskStatus]);
};
